import {
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompanyAccount } from '../queryKeys';

import { fetchCompanyAccountSupplierLetter } from './fetchCompanyAccountSupplierLetter';
import { type SupplierLetterMeta } from './types';

/**
 * Generates the query options for the CompanyAccount supplier letter query.
 *
 * @param companyAccountId - The ID of the company account.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_account_supplier_letter_vadmin_company_accounts__company_account_id__supplier_letter_get
 */
// fallow-ignore-next-line unused-export
export const getCompanyAccountSupplierLetterQueryOptions = <
  TData = SupplierLetterMeta | null,
>({
  companyAccountId,
  options,
}: {
  companyAccountId: UUID | undefined;
  options?: Omit<
    UseQueryOptions<SupplierLetterMeta | null, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: companyAccountId
      ? async () => fetchCompanyAccountSupplierLetter(companyAccountId)
      : skipToken,
    queryKey: companyAccountId
      ? queryKeysCompanyAccount.getSupplierLetter(companyAccountId)
      : queryKeysCompanyAccount.getSupplierLetters(),
    staleTime: ms('1h'),
    ...options,
  });

/**
 * React Query based custom hook for getting the supplier letter for a CompanyAccount.
 * Resolves to `null` when no letter is configured (404).
 *
 * @param companyAccountId - The ID of the company account.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Letter/operation/get_company_account_supplier_letter_vadmin_company_accounts__company_account_id__supplier_letter_get
 */
export const useQueryCompanyAccountSupplierLetter = <
  TData = SupplierLetterMeta | null,
>(
  companyAccountId: UUID | undefined,
  options?: Omit<
    UseQueryOptions<SupplierLetterMeta | null, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getCompanyAccountSupplierLetterQueryOptions<TData>({
      companyAccountId,
      options,
    }),
  );
